import { forwardRef } from '@chakra-ui/react'
import { css, Global } from '@emotion/react'
import { lazy, Suspense, useMemo } from 'react'

const JoditEditor = lazy(() => import('jodit-react'))

import {
  TextAlignCenterString,
  TextAlignJustifyString,
  TextAlignLeftString,
  TextAlignRightString,
  TextBoldString,
  TextItalicsString,
  TextOrderedListString,
  TextUnorderedListString,
} from '../../icons'
import { Loading } from '../Loading'
import { cs_cz } from './cs_cz'

const getCustomIcon = (name: string, control: any, clearName: string) => {
  switch (clearName) {
    case 'bold':
      return TextBoldString
    case 'italic':
      return TextItalicsString

    case 'image':
      return

    case 'left':
      return TextAlignLeftString
    case 'right':
      return TextAlignRightString
    case 'justify':
      return TextAlignJustifyString
    case 'center':
      return TextAlignCenterString

    case 'ul':
      return TextUnorderedListString
    case 'ol':
      return TextOrderedListString
  }
}

export const TextEditor = forwardRef(
  ({
    placeholder,
    toolbar = true,
    minHeight = '100px',
    ...props
  }: {
    placeholder?: string
    toolbar?: boolean
    value: string
    minHeight?: string
    onBlur?: (value: string) => void
    onChange?: (value: string) => void
  }, ref) => {
    const config = useMemo(() => {
      const config = {
        readonly: false, // all options from https://xdsoft.net/jodit/docs/,
        placeholder: (placeholder as any) || 'Vložte text...',
        statusbar: false,
        font: '12px Inter',
        width: '100%',
        maxWidth: '100%',
        minHeight,
        language: 'cs_cz',
        i18n: {
          cs_cz,
        },
        events: {
          getIcon: getCustomIcon,
        },
        toolbar,
        disablePlugins: ['add-new-line'],
      }
      return config
    }, [placeholder])

    return (
      <>
        <Global
          styles={css`
            .jodit-react-container {
              width: 100%;
            }

            .jodit-container:not(.jodit_inline) {
              border-color: var(--ce-colors-neutral-100) !important;
              border-radius: var(--ce-radii-sm) !important;
              border: 1px dashed var(--ce-colors-neutral-200) !important;
            }

            .jodit-workplace {
              border-radius: var(--ce-radii-sm) !important;
              background-color: white !important;
            }

            .jodit-toolbar__box:not(:empty),
            .jodit-toolbar__box {
              background-color: var(--ce-colors-neutral-50) !important;
              border-radius: var(--ce-radii-sm) var(--ce-radii-sm) 0 0 !important;
            }

            .jodit-toolbar-button .jodit-icon {
              width: 1rem !important;
              height: 1rem !important;
            }

            svg.jodit-icon {
              fill: var(--ce-colors-primary-700);
              stroke: var(--ce-colors-primary-700);
              stroke-width: 0 !important;
            }

            .jodit-add-new-line > span {
              background-color: var(--ce-colors-neutral-50) !important;
              border-color: var(--ce-colors-neutral-200) !important;
              border-radius: var(--ce-radii-sm) !important;
              margin-bottom: .5rem !important;
              margin-left: -.5rem !important;
            }
          `}
        />

        <Suspense fallback={<Loading />}>
          <JoditEditor
            ref={ref}
            config={config}
            {...props}
          />
        </Suspense>
      </>
    )
  },
)
